import { Link } from 'react-router-dom'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { CoreFeature } from '@/lib/gcc-content'
import { SlideUp, FadeIn } from '@/components/animations'
import { motion } from 'framer-motion'
import { useReducedMotion } from '@/hooks/useReducedMotion'

interface CoreFeaturesSectionProps {
  features: CoreFeature[]
  title?: string
  className?: string
}

interface FeatureCardProps {
  feature: CoreFeature
}

const featureImages: Record<string, string> = {
  'agent-marketplace': '/images/home-core-features/agent-marketplace.jpeg',
  'tools': '/images/home-core-features/tools.jpeg',
  'thought-leadership': '/images/home-core-features/thought-leadership.jpeg',
  'provider-ecosystem': '/images/home-core-features/provider-ecosystem.jpeg'
}

const featureActions: Record<string, { primary: { label: string; href: string }; secondary?: { label: string; href: string } }> = {
  'agent-marketplace': {
    primary: { label: 'Explore Orbyt', href: '/marketplace' },
    secondary: { label: 'About Orbyt', href: '/agentsphere' }
  },
  'tools': {
    primary: { label: 'Explore Tools', href: '/tools' }
  },
  'thought-leadership': {
    primary: { label: 'Explore Thought Leadership', href: '/thought-leadership' },
    secondary: { label: 'Browse AI Thoughtbook', href: '/agentic-ai-thoughtbook' }
  },
  'provider-ecosystem': {
    primary: { label: 'Explore Directory', href: '/providers/directory' },
    secondary: { label: 'About Provider Listings', href: '/providers' }
  }
}

const FeatureCard = ({ feature }: FeatureCardProps) => {
  const prefersReducedMotion = useReducedMotion()
  const imageSrc = featureImages[feature.id]
  const actions = featureActions[feature.id]

  return (
    <Card
      data-testid="feature-card"
      className="group h-full overflow-hidden border border-slate-200/80 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
    >
      <div className="flex flex-col md:flex-row h-full">
        {imageSrc && (
          <motion.div
            className="relative flex items-center justify-center md:w-56 lg:w-64 flex-shrink-0 bg-white overflow-hidden p-6 md:p-8"
            whileHover={prefersReducedMotion ? undefined : { scale: 1.02 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
          >
            <img
              src={imageSrc}
              alt={`${feature.title} illustration`}
              className="mx-auto w-full max-h-40 object-contain transition-transform duration-300 group-hover:scale-105"
            />
          </motion.div>
        )}

        <div className="flex-1 p-6 md:p-8 flex flex-col">
          <h3 className="text-2xl font-semibold text-slate-900 leading-tight mb-3">
            {feature.title}
          </h3>
          <p className="text-slate-600 leading-relaxed mb-6">
            {feature.description}
          </p>
          <ul className="space-y-3 flex-1">
            {feature.details.map((detail, index) => (
              <li key={index} className="flex items-start gap-3 text-sm text-slate-600 leading-relaxed">
                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-slate-400 flex-shrink-0" />
                <span>{detail}</span>
              </li>
            ))}
          </ul>

          {actions && (
            <div className="mt-auto pt-6 flex flex-wrap gap-3">
              <Button
                asChild
                className="rounded-full bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-md hover:shadow-lg hover:from-blue-700 hover:to-purple-700"
              >
                <Link to={actions.primary.href}>{actions.primary.label}</Link>
              </Button>

              {actions.secondary && (
                <Button
                  asChild
                  variant="outline"
                  className="rounded-full border-slate-300 text-slate-700 hover:bg-slate-50"
                >
                  <Link to={actions.secondary.href}>{actions.secondary.label}</Link>
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </Card>
  )
}

const CoreFeaturesSection = ({ features, title = "Core Features", className = '' }: CoreFeaturesSectionProps) => {
  return (
    <section
      data-testid="core-features"
      className={`py-24 px-4 sm:px-6 lg:px-8 ${className}`}
    >
      <div className="max-w-7xl mx-auto">
        {/* Section Title */}
        <FadeIn className="text-center mb-20">
          <h2 className="text-3xl font-light text-slate-900 lg:text-4xl tracking-tight">
            {title}
          </h2>
        </FadeIn>

        {/* Features Grid */}
        <div
          data-testid="features-grid"
          className="grid grid-cols-1 gap-10 md:grid-cols-2 xl:gap-12"
        >
          {features.map((feature, index) => (
            <SlideUp
              key={feature.id}
              delay={index * 0.15}
              duration={0.6}
              distance={40}
              className="h-full"
            >
              <FeatureCard feature={feature} />
            </SlideUp>
          ))}
        </div>
      </div>
    </section>
  )
}

export default CoreFeaturesSection
